import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useParams } from "react-router-dom";

import styles from "./css/Valorame.module.css";
import ciemLogo from "../assets/CIEM-logo.png";

import Question00           from "../components/Question00";
import Question01           from "../components/Question01";
import Question02           from "../components/Question02";
import Question03           from "../components/Question03";
import QuestionMessageFinal from "../components/QuestionMessageFinal";
import Thanks               from "../components/Thanks";
import Modalities           from "../components/Modalities";
import QuestionContact      from "../components/QuestionContact";
import QuestionCustom       from "../components/QuestionCustom";

const typesModalities = { CR: "CR", US: "US", DM: "DM", MG: "MG", CT: "CT", MR: "MR" };
const TOTAL_STEPS = 6;
const stepMap = { 0: 0, 1: 1, 11: 2, 2: 3, 3: 4, 4: 4, 6: 5, 8: 6, 9: 0 };

const initialModalities = [
	{ modality: "CR", active: false, value: 1 },
	{ modality: "US", active: false, value: 2 },
	{ modality: "DM", active: false, value: 3 },
	{ modality: "MG", active: false, value: 4 },
	{ modality: "CT", active: false, value: 5 },
	{ modality: "MR", active: false, value: 6 },
];

function Valorame() {
	const { mode, access } = useParams();

	const [avance, setAvance]           = useState(0);
	const [referenceCustomModality, setReferenceCustomModality] = useState(false);
	const [modalities, setModalities]   = useState(initialModalities);
	const [referencedByApp, setReferencedByApp] = useState(false);
	const [resultModalities, setResultModalities] = useState([]);
	const [handleEvent, setHandleEvent] = useState({
		numeroAcceso: access,
		value01: -1, value02: -1, value03: -1,
		value04: "", phone: "", patient: "",
	});

	useEffect(() => {
		if (access) {
			setModalities(prev => prev.map(item =>
				item.modality === mode ? { ...item, active: true } : item
			));
			setReferencedByApp(true);
		}
	}, [access, mode]);

	const currentStep  = stepMap[avance] ?? 0;
	const showProgress = avance !== 0 && avance !== 9;

	return (
		<div className={styles.container}>
			<motion.div
				className={styles.card}
				initial={{ opacity: 0, y: 28, scale: 0.97 }}
				animate={{ opacity: 1, y: 0,  scale: 1   }}
				transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
			>
				{/* ── Logo strip ───────────────────────── */}
				<div className={styles.logoStrip}>
					<motion.img
						src={ciemLogo}
						alt="CIEM Radiólogos"
						className={styles.cardLogo}
						initial={{ opacity: 0, y: -8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.18, duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
					/>
					<div className={styles.logoRule} />
				</div>

				{/* ── Progress bar ─────────────────────── */}
				{showProgress && (
					<div className={styles.progressBar}>
						{Array.from({ length: TOTAL_STEPS }, (_, i) => (
							<motion.div
								key={i}
								className={styles.progressSegment}
								animate={{
									background: i < currentStep
										? "linear-gradient(90deg, #11847d, #48c1ba)"
										: "#e2eceb",
								}}
								transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
							/>
						))}
					</div>
				)}

				{/* ── Step content ─────────────────────── */}
				<main className={styles.main}>
					<AnimatePresence mode="wait">
						{avance === 0 && <Question00 key="q00" setAvance={setAvance} />}

						{avance === 1 && (
							<QuestionContact key="q01"
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
							/>
						)}

						{avance === 11 && (
							<Question01 key="q11"
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
							/>
						)}

						{avance === 2 && (
							<Question02 key="q02"
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
								referencedByApp={referencedByApp}
							/>
						)}

						{!referencedByApp && avance === 3 && (
							<Modalities key="qmod"
								modalidades={modalities}
								setModalidades={setModalities}
								typesModalities={typesModalities}
								setAvance={setAvance}
								setReferenceCustomModality={setReferenceCustomModality}
							/>
						)}

						{(referencedByApp || referenceCustomModality) && avance === 4 && (
							<QuestionCustom key="qcustom"
								modalities={modalities}
								resultModalities={resultModalities}
								setResultModalities={setResultModalities}
								setModalities={setModalities}
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
							/>
						)}

						{avance === 6 && (
							<Question03 key="q06"
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
							/>
						)}

						{avance === 8 && (
							<QuestionMessageFinal key="q08"
								setHandleEvent={setHandleEvent}
								setAvance={setAvance}
								data={handleEvent}
								setModalities={setModalities}
								resultModalities={resultModalities}
							/>
						)}

						{avance === 9 && <Thanks key="q09" />}
					</AnimatePresence>
				</main>
			</motion.div>
		</div>
	);
}

export default Valorame;
